import React from 'react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { TrajectoryYearSnapshot } from '../utils/financialEngine';

interface TrajectoryChartProps {
  trajectory: TrajectoryYearSnapshot[];
}

const formatCurrency = (val: number) => {
  return `$${(val / 1000).toFixed(0)}k`;
};

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="rounded-xl bg-slate-950/95 border border-slate-800 p-3 shadow-2xl font-sans text-xs space-y-1.5 min-w-[200px]">
        <div className="font-bold text-slate-200 border-b border-slate-800 pb-1 flex justify-between">
          <span>Year {label}</span>
          <span className="font-mono text-cyan-400">Net Worth</span>
        </div>
        {payload.map((entry: any, index: number) => (
          <div key={`item-${index}`} className="flex justify-between items-center font-mono">
            <span className="flex items-center gap-1.5" style={{ color: entry.color }}>
              <span className="w-2 h-2 rounded-full" style={{ backgroundColor: entry.color }} />
              <span className="font-sans text-slate-300">{entry.name}:</span>
            </span>
            <span className="font-bold text-white">${Math.round(entry.value).toLocaleString()}</span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

export const TrajectoryChart: React.FC<TrajectoryChartProps> = ({ trajectory }) => {
  const data = trajectory.map((t) => ({
    year: `Yr ${t.year}`,
    'Keep & Rent NW': t.keepRent.netWorth,
    'Sell & Buy NW': t.sellBuy.netWorth,
    rawYear: t.year
  }));

  return (
    <div className="rounded-2xl bg-gradient-to-br from-slate-900 via-slate-900 to-slate-950 p-5 border border-slate-800/80 shadow-xl space-y-3">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-800/60 pb-3">
        <div>
          <h3 className="font-heading text-base font-bold text-white flex items-center gap-2">
            10-Year Net Worth Trajectory Projection
          </h3>
          <p className="text-xs text-slate-400">Side-by-side wealth accumulation graph (Appreciation + Amortization + Excess Cash Yield)</p>
        </div>
        <div className="flex items-center gap-4 text-xs font-mono">
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-cyan-400" />
            <span className="text-slate-300">Keep & Rent</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-3 h-3 rounded-full bg-emerald-400" />
            <span className="text-slate-300">Sell & Buy</span>
          </div>
        </div>
      </div>

      <div className="h-[320px] w-full pt-2">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
            <defs>
              <linearGradient id="colorKeep" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#22d3ee" stopOpacity={0.4} />
                <stop offset="95%" stopColor="#22d3ee" stopOpacity={0.0} />
              </linearGradient>
              <linearGradient id="colorSell" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#34d399" stopOpacity={0.3} />
                <stop offset="95%" stopColor="#34d399" stopOpacity={0.0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
            <XAxis dataKey="year" stroke="#64748b" tick={{ fontSize: 11 }} />
            <YAxis stroke="#64748b" tickFormatter={formatCurrency} tick={{ fontSize: 11 }} />
            <Tooltip content={<CustomTooltip />} />
            <Area
              type="monotone"
              dataKey="Keep & Rent NW"
              stroke="#22d3ee"
              strokeWidth={3}
              fillOpacity={1}
              fill="url(#colorKeep)"
            />
            <Area
              type="monotone"
              dataKey="Sell & Buy NW"
              stroke="#34d399"
              strokeWidth={3}
              fillOpacity={1}
              fill="url(#colorSell)"
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
